import React, { useCallback, useMemo, useState } from 'react'
import HistoricoDiarioTable from '../../components/helpdesk/HistoricoDiarioTable'
import { comercialContatosPortalService } from '../../services/comercialContatosPortal'
import type { DiarioRelatorioRow, DiaryRelatorioFiltros } from '../../types/comercialContatosPortal'
import {
  ContatosPortalCorretorFiltroInput,
  ContatosPortalFiltrosSection,
  ContatosPortalPeriodoFiltroInput,
  ContatosPortalResponsavelFiltroInput,
  FiltroField,
} from './ContatosPortalFiltros'
import { ContatosPortalTableSection } from './ContatosPortalTableSection'
import { useContatosPortalFiltros } from './useContatosPortalFiltros'

const PAGE_SIZE = 20

const ContatosPortalDiarioTab: React.FC = () => {
  const {
    isComercialFilterRestricted,
    loadingComercialUsers,
    comerciaisOptions,
    brokerId,
    selectedBroker,
    setCorretor,
    setComercialUserId,
    periodo,
    setPeriodo,
    periodoInvalido,
    setPeriodoInvalido,
    displayedComercialUserId,
    effectiveComercialUserId,
    validatePeriodo,
    resetFiltrosBase,
  } = useContatosPortalFiltros()

  const [rows, setRows] = useState<DiarioRelatorioRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])

  const buildFiltros = useCallback((): DiaryRelatorioFiltros => {
    const filtros: DiaryRelatorioFiltros = {}
    if (brokerId) filtros.broker_id = brokerId
    if (effectiveComercialUserId && effectiveComercialUserId !== '\0') {
      filtros.comercial_user_id = effectiveComercialUserId
    }
    filtros.data_inicio = periodo.dataInicio
    filtros.data_fim = periodo.dataFim
    return filtros
  }, [brokerId, effectiveComercialUserId, periodo])

  const carregar = useCallback(
    async (page = 1) => {
      setLoading(true)
      setError(null)
      try {
        const periodoErro = validatePeriodo()
        if (periodoErro) {
          setPeriodoInvalido(true)
          setError(periodoErro)
          setRows([])
          setTotal(0)
          setHasSearched(true)
          return
        }
        setPeriodoInvalido(false)
        const filtros = buildFiltros()
        const resultado = await comercialContatosPortalService.listDiaryEntriesForRelatorio(
          filtros,
          page,
          PAGE_SIZE
        )
        if (resultado.error) {
          setError(`Não foi possível carregar o histórico de diário: ${resultado.error}`)
          setRows([])
          setTotal(0)
          setHasSearched(true)
          return
        }
        setRows(resultado.data)
        setTotal(resultado.total)
        setCurrentPage(resultado.page)
        setHasSearched(true)
      } catch (e) {
        console.error('[ContatosPortalDiarioTab]', e)
        setError('Não foi possível carregar o histórico de diário.')
        setRows([])
        setTotal(0)
        setHasSearched(true)
      } finally {
        setLoading(false)
      }
    },
    [buildFiltros, setPeriodoInvalido, validatePeriodo]
  )

  const handleBuscar = () => {
    setCurrentPage(1)
    void carregar(1)
  }

  const handleLimparFiltros = () => {
    resetFiltrosBase()
    setError(null)
    setHasSearched(false)
    setRows([])
    setTotal(0)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    if (loading) return
    void carregar(page)
  }

  return (
    <div className="flex flex-col">
      <ContatosPortalFiltrosSection onBuscar={handleBuscar} onLimpar={handleLimparFiltros} loading={loading}>
        <FiltroField label="Comercial responsável">
          <ContatosPortalResponsavelFiltroInput
            value={displayedComercialUserId}
            onChange={setComercialUserId}
            usuarios={comerciaisOptions}
            disabled={loadingComercialUsers || isComercialFilterRestricted}
            restricted={isComercialFilterRestricted}
          />
        </FiltroField>
        <FiltroField label="Corretor">
          <ContatosPortalCorretorFiltroInput
            value={brokerId}
            initialBroker={selectedBroker}
            onChange={setCorretor}
          />
        </FiltroField>
        <ContatosPortalPeriodoFiltroInput
          layout="grid"
          value={periodo}
          onChange={next => {
            setPeriodo(next)
            setPeriodoInvalido(false)
          }}
          invalid={periodoInvalido}
        />
      </ContatosPortalFiltrosSection>

      <ContatosPortalTableSection
        loading={loading}
        error={hasSearched ? error : null}
        hasSearched={hasSearched}
        isEmpty={rows.length === 0}
        emptyBeforeSearchMessage="Use os filtros e clique em Buscar para carregar o histórico de diário."
        currentPage={currentPage}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
      >
        <HistoricoDiarioTable rows={rows} />
      </ContatosPortalTableSection>
    </div>
  )
}

export default ContatosPortalDiarioTab
